import React, { useEffect } from 'react';
import { privacyNoticeHTML } from './privacy.js';
import { termsHTML } from './terms.js';
import { cookieHTML } from './cookie.js';
import useLegalModal from './useLegalModal';
import LegalModal from './LegalModal';
import { WHITE_LABEL_CONFIG } from './white_label';

const SuccessPage = () => {
  const { modalOpen, modalType, openModal, closeModal, openCookieSettings } = useLegalModal();

  useEffect(() => {
    document.title = `${WHITE_LABEL_CONFIG.BRAND_NAME} | Purchase Confirmed`;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        `${WHITE_LABEL_CONFIG.SCHOOL_DIAGNOSTIC_LABEL} purchase confirmed. Check your email for your digital timed mock and next steps.`
      );
    }
  }, []);

  // Set current year in footer
  useEffect(() => {
    const yearSpan = document.getElementById('currentYear');
    if (yearSpan) {
      yearSpan.textContent = new Date().getFullYear();
    }
  }, []);

  return (
    <div className="bg-slate-50 min-h-screen flex flex-col font-sans text-gray-900">
      <div className="flex-grow container mx-auto px-4 py-8 md:py-12 max-w-4xl">
        {/* Success Header */}
        <div className="text-center mb-10 md:mb-16">
          <img
            src={WHITE_LABEL_CONFIG.BRAND_LOGO_SRC}
            alt={`${WHITE_LABEL_CONFIG.BRAND_NAME} logo`}
            className="h-6 sm:h-7 w-auto mb-5 sm:mb-6"
          />
          <p className="inline-flex items-center rounded-full border border-gold-300 bg-gold-50 px-4 py-2 text-xs sm:text-sm font-medium text-navy-900 mb-6 max-w-full">
            {WHITE_LABEL_CONFIG.SCHOOL_DIAGNOSTIC_LABEL} purchase confirmed
          </p>
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-gray-900 mb-4 text-balance">
            You're in. Your child's diagnostic process starts now.
          </h1>
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-100 mb-4">
            <i className="fas fa-check-circle text-6xl text-emerald-500"></i>
          </div>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Your timed mock PDF will be emailed shortly, and the next step is to send the completed answers back for marking and review.
          </p>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-5 sm:p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">What happens next</h2>
          <p className="text-gray-600 mb-8">Follow these three simple steps to receive marked answers, your parent report, and timeline-aware next-step recommendations within 7 working days of a clear submission.</p>

          {/* 3-Step Process */}
          <div className="space-y-8">
            {/* Step 1 */}
            <div className="flex items-start gap-4 md:gap-6">
              <div className="step-icon shrink-0 bg-gradient-to-br from-gold-400 via-gold-500 to-amber-500 text-navy-950 shadow-[0_10px_24px_rgba(184,134,11,0.22)] border border-gold-300/70">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Check your email</h3>
                <p className="text-gray-700">
                  Your digital diagnostic pack will be prepared and sent within 3 working days (usually sooner). <span className="font-medium text-amber-600">(Keep an eye on your inbox and junk folder just in case.)</span>
                </p>
              </div>
              <div className="hidden md:flex md:ml-auto shrink-0 self-center items-center justify-center w-14 h-14 rounded-2xl bg-gold-50 text-gold-700 border border-gold-200 shadow-sm">
                <i className="fas fa-envelope-open-text text-2xl"></i>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-4 md:gap-6">
              <div className="step-icon shrink-0 bg-gradient-to-br from-gold-400 via-gold-500 to-amber-500 text-navy-950 shadow-[0_10px_24px_rgba(184,134,11,0.22)] border border-gold-300/70">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Sit the test</h3>
                <p className="text-gray-700">
                  Have your child complete the timed assessment in a quiet room with no distractions.
                </p>
              </div>
              <div className="hidden md:flex md:ml-auto shrink-0 self-center items-center justify-center w-14 h-14 rounded-2xl bg-navy-50 text-navy-800 border border-navy-200 shadow-sm">
                <i className="fas fa-user-graduate text-2xl"></i>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start gap-4 md:gap-6">
              <div className="step-icon shrink-0 bg-gradient-to-br from-gold-400 via-gold-500 to-amber-500 text-navy-950 shadow-[0_10px_24px_rgba(184,134,11,0.22)] border border-gold-300/70">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Send it back for marking</h3>
                <p className="text-gray-700">
                  Reply to our email with a photo or screenshot of the completed answers and tell us when the real exam is. We will return marked answers, a personalised parent report, and recommendations matched to your timeline within 7 working days of a clear submission.
                </p>
              </div>
              <div className="hidden md:flex md:ml-auto shrink-0 self-center items-center justify-center w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-sm">
                <i className="fas fa-chart-line text-2xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Mastery Hook */}
        <div className="bg-gradient-to-r from-navy-50 to-gold-50 border border-gold-200 rounded-2xl p-5 sm:p-6 md:p-8 mb-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="md:w-1/4 text-center md:text-left">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white shadow-soft mb-4">
                <i className="fas fa-graduation-cap text-3xl text-navy-900"></i>
              </div>
            </div>
            <div className="md:w-3/4">
              <h3 className="text-2xl font-serif font-bold text-gray-900 mb-3">What this diagnostic should help you decide</h3>
              <p className="text-base sm:text-lg text-gray-800 mb-4">
                Once your report is in your inbox, you should have a clearer view of whether your child looks strong, borderline, or in need of a different prep approach. That helps you make better decisions before spending more time or money in the wrong areas.
              </p>
              <div className="flex items-center justify-center md:justify-start text-gray-700 text-center md:text-left">
                <i className="fas fa-clock mr-2"></i>
                <span className="font-medium">Current turnaround: within 7 working days of a clear submission.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Support Footer */}
        <div className="text-center text-gray-600">
          <p className="mb-2">
            <i className="fas fa-question-circle mr-2"></i> Questions? Email&nbsp;
            <a href={`mailto:${WHITE_LABEL_CONFIG.SUPPORT_EMAIL}`} className="text-navy-900 font-medium hover:underline">
              {WHITE_LABEL_CONFIG.SUPPORT_EMAIL}
            </a>
          </p>
          <div className="text-sm mt-4 flex justify-center gap-x-4 md:gap-x-6 gap-y-2 flex-wrap">
             <a 
               href="#terms"
               onClick={(e) => {
                 e.preventDefault();
                 openModal('terms');
               }}
                className="text-navy-900 hover:underline"
             >
               Terms of Service
             </a>
             <a 
               href="#privacy"
               onClick={(e) => {
                 e.preventDefault();
                 openModal('privacy');
               }}
                className="text-navy-900 hover:underline"
             >
               Privacy Policy
             </a>
             <a 
               href="#cookies"
               onClick={(e) => {
                 e.preventDefault();
                 openModal('cookie');
               }}
                className="text-navy-900 hover:underline"
             >
               Cookie Policy
             </a>
            <button 
              onClick={openCookieSettings}
               className="text-navy-900 hover:underline"
            >
              Cookie Settings
            </button>
          </div>
          <p className="text-sm mt-4">
            &copy; <span id="currentYear"></span> {WHITE_LABEL_CONFIG.BRAND_NAME} | {WHITE_LABEL_CONFIG.FOOTER_TAGLINE}
          </p>
          <p className="text-sm">
            {WHITE_LABEL_CONFIG.BRAND_LEGAL_NAME} is a provider of independent academic benchmarking.
          </p>
        </div>
      </div>

      <LegalModal
        modalOpen={modalOpen}
        modalType={modalType}
        closeModal={closeModal}
        termsHTML={termsHTML}
        privacyNoticeHTML={privacyNoticeHTML}
        cookieHTML={cookieHTML}
      />
    </div>
  );
};

export default SuccessPage;
